import React, { useEffect, useState } from "react";
import {
  adminLogin, adminLogout, adminMe,
  adminListReports, adminDecideReport, adminRestorePlayer,
  adminDisputesCount, adminListTickets, adminWipeBillingPlans, adminCleanupGoLive,
} from "../lib/api";
import { ReportsSection } from "./admin/ReportsSection";
import { LeaguesSection } from "./admin/LeaguesSection";
import { DisputesSection } from "./admin/DisputesSection";
import { ReferralsSection } from "./admin/ReferralsSection";
import { SeasonPassSection } from "./admin/SeasonPassSection";
import { AuditSection } from "./admin/AuditSection";
import { OnchainSection } from "./admin/OnchainSection";
import { PayoutsSection } from "./admin/PayoutsSection";
import { UsersSection } from "./admin/UsersSection";
import { BroadcastSection } from "./admin/BroadcastSection";
import { AnalyticsSection } from "./admin/AnalyticsSection";
import { TicketsSection } from "./admin/TicketsSection";
import { VouchersSection } from "./admin/VouchersSection";

/**
 * AdminPanel — moderation home for LEAGUE HUB. Password-only session
 * (separate `admin_sessions` collection, 12h sliding) decoupled from
 * regular user auth. Each section lives in `./admin/*.js`.
 */
export function AdminPanel({ onToast }) {
  const [authed, setAuthed] = useState(null);   // null | true | false
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ reports: [], counts: { open: 0, dismissed: 0, actioned: 0 } });
  const [tab, setTab] = useState("open");
  const [decisionBusy, setDecisionBusy] = useState("");
  const [section, setSection] = useState("reports");
  const [disputeCount, setDisputeCount] = useState(0);
  const [ticketOpenCount, setTicketOpenCount] = useState(0);

  // Poll open-disputes count every 30s when authed so the nav badge stays fresh.
  useEffect(() => {
    if (!authed) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await adminDisputesCount();
        if (!cancelled) setDisputeCount(r?.open || 0);
      } catch { /* ignore — badge is best-effort */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [authed]);

  useEffect(() => {
    if (!authed) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await adminListTickets("open");
        if (!cancelled) setTicketOpenCount(r?.counts?.open || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 45000);
    return () => { cancelled = true; clearInterval(id); };
  }, [authed]);

  const refreshAuth = async () => {
    try {
      const r = await adminMe();
      setAuthed(!!r.authenticated);
    } catch { setAuthed(false); }
  };

  useEffect(() => { refreshAuth(); }, []);

  const reload = async () => {
    try { setData(await adminListReports(tab)); }
    catch (e) {
      if (e?.response?.status === 401) setAuthed(false);
      else onToast?.(e?.response?.data?.detail || "Couldn't load reports.");
    }
  };

  useEffect(() => { if (authed) reload(); /* eslint-disable-next-line */ }, [authed, tab]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(pw);
      setPw("");
      setAuthed(true);
      onToast?.("🛡 Admin signed in.");
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Wrong password.");
    } finally { setBusy(false); }
  };

  const logout = async () => {
    await adminLogout().catch(() => {});
    setAuthed(false);
    setData({ reports: [], counts: { open: 0, dismissed: 0, actioned: 0 } });
    onToast?.("Signed out of admin.");
  };

  const wipeBillingPlans = async () => {
    const ok = window.confirm(
      "Wipe all cached Stripe price IDs from `billing_plans`?\n\n" +
      "Use this ONCE after flipping Stripe from test mode to live mode. " +
      "On the next $1.99 PRO checkout, fresh live-mode prices will be " +
      "lazy-recreated and re-cached. Safe to run multiple times.",
    );
    if (!ok) return;
    try {
      const r = await adminWipeBillingPlans();
      onToast?.(`Wiped ${r.deleted} cached price row(s). Run a real checkout to recreate.`);
    } catch (e) {
      if (e?.response?.status === 401) setAuthed(false);
      onToast?.(e?.response?.data?.detail || "Wipe failed.");
    }
  };

  const cleanupGoLive = async () => {
    let preview;
    try {
      preview = await adminCleanupGoLive(false);
    } catch (e) {
      if (e?.response?.status === 401) setAuthed(false);
      onToast?.(e?.response?.data?.detail || "Preview failed.");
      return;
    }
    const w = preview?.would_delete || {};
    const ok = window.confirm(
      `GO-LIVE CLEANUP — DRY RUN PREVIEW\n\n` +
      `This will permanently DELETE:\n` +
      `  • ${w.leagues || 0} non-sandbox leagues\n` +
      `  • ${w.tournaments || 0} tournaments inside them\n` +
      `  • ${w.bot_teams || 0} bot teams (no real human members)\n` +
      `  • ${w.orphan_bot_users || 0} orphan bot accounts\n` +
      `  • All associated applications, invites, challenges, messages\n\n` +
      `KEPT INTACT:\n` +
      `  • Every sandbox league (private playgrounds)\n` +
      `  • Bots currently inside any sandbox\n` +
      `  • Every team with at least ONE real human member\n` +
      `  • All human user accounts\n` +
      `  • Billing, audit, score history\n\n` +
      `Proceed?`
    );
    if (!ok) return;
    try {
      const r = await adminCleanupGoLive(true);
      const d = r?.deleted || {};
      onToast?.(`🧹 Cleanup done: ${d.leagues} leagues, ${d.bot_teams} bot teams, ${d.orphan_bot_users} bots, ${d.tournaments} tournaments removed.`);
    } catch (e) {
      if (e?.response?.status === 401) setAuthed(false);
      onToast?.(e?.response?.data?.detail || "Cleanup failed.");
    }
  };

  const decide = async (report_id, action) => {
    let note = "";
    if (action === "remove_player") {
      const ok = window.confirm(
        "Suspend this account? This terminates all their sessions and blocks future sign-ins. You can restore them later."
      );
      if (!ok) return;
      note = window.prompt("Optional moderator note (visible in audit log):", "") || "";
    } else if (action === "strip_image") {
      const ok = window.confirm(
        "Strip the reported image from this player/team's profile? The account stays active — just the image is cleared."
      );
      if (!ok) return;
      note = window.prompt("Optional moderator note (visible in audit log):", "") || "";
    }
    setDecisionBusy(report_id);
    try {
      const r = await adminDecideReport(report_id, action, note);
      const successMsg =
        action === "remove_player" ? "💥 Player suspended (sessions terminated)."
        : action === "strip_image" ? `🖼️ Image stripped (${r.stripped || "ok"}).`
        : "Report dismissed.";
      onToast?.(successMsg);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't action report.");
    } finally { setDecisionBusy(""); }
  };

  const restore = async (uid) => {
    if (!window.confirm("Restore this player's account?")) return;
    try {
      await adminRestorePlayer(uid);
      onToast?.("✅ Player restored.");
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't restore.");
    }
  };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center text-white/60 text-sm">Loading…</div>;
  }

  if (!authed) {
    return (
      <main className="page-z min-h-screen flex items-center justify-center px-4">
        <form
          onSubmit={submitLogin}
          data-testid="admin-login-form"
          className="glass-strong rounded-3xl p-8 w-full max-w-sm border-2 border-[#14b8a6]/40"
        >
          <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
            <i className="fas fa-shield-halved mr-1" />ADMIN PANEL
          </p>
          <h2 className="font-display text-3xl mt-2">SIGN IN</h2>
          <p className="text-[11px] text-white/55 mt-1">
            Password-only access. This panel is separate from your player
            account.
          </p>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            required
            placeholder="Admin password"
            data-testid="admin-password-input"
            className="mt-5 w-full bg-black/50 border border-[#14b8a6]/40 rounded-xl px-4 py-3 text-white"
          />
          <button
            type="submit"
            disabled={busy || !pw}
            data-testid="admin-login-submit"
            className="mt-3 w-full rounded-xl bg-[#14b8a6] text-black font-bold py-3 text-xs tracking-widest hover:brightness-110 disabled:opacity-60"
          >
            {busy ? "..." : "🛡 SIGN IN"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="page-z max-w-screen-xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
            <i className="fas fa-shield-halved mr-1" />ADMIN PANEL
          </p>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl grad-text">MODERATION</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={cleanupGoLive}
            data-testid="admin-cleanup-golive-btn"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
            title="Wipe seed/test leagues + orphan bots. Sandbox + real users untouched."
          >
            <i className="fas fa-broom mr-1" />GO-LIVE CLEANUP
          </button>
          <button
            type="button"
            onClick={wipeBillingPlans}
            data-testid="admin-wipe-billing-btn"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
            title="Run once after switching Stripe to live mode"
          >
            <i className="fas fa-broom mr-1" />WIPE BILLING PLANS
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
          >
            <i className="fas fa-right-from-bracket mr-1" />SIGN OUT
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-6" data-testid="admin-section-tabs">
        <button
          type="button"
          onClick={() => setSection("reports")}
          data-testid="admin-section-reports"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "reports" ? "btn-neon" : "btn-ghost"}`}
        >
          REPORTS · {data?.counts?.open || 0}
        </button>
        <button
          type="button"
          onClick={() => setSection("tickets")}
          data-testid="admin-section-tickets"
          className={`relative text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "tickets" ? "btn-neon" : "btn-ghost"}`}
        >
          TICKETS
          {ticketOpenCount > 0 && (
            <span
              data-testid="admin-tickets-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#14b8a6] text-black text-[9px] font-bold flex items-center justify-center border border-black/50 shadow"
            >
              {ticketOpenCount > 99 ? "99+" : ticketOpenCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setSection("leagues")}
          data-testid="admin-section-leagues"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "leagues" ? "btn-neon" : "btn-ghost"}`}
        >
          LEAGUES
        </button>
        <button
          type="button"
          onClick={() => setSection("disputes")}
          data-testid="admin-section-disputes"
          className={`relative text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "disputes" ? "btn-neon" : "btn-ghost"}`}
        >
          DISPUTES
          {disputeCount > 0 && (
            <span
              data-testid="admin-disputes-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#14b8a6] text-black text-[9px] font-bold flex items-center justify-center border border-black/50 shadow"
            >
              {disputeCount > 99 ? "99+" : disputeCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setSection("referrals")}
          data-testid="admin-section-referrals"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "referrals" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-gift mr-1" />REFERRALS
        </button>
        <button
          type="button"
          onClick={() => setSection("season")}
          data-testid="admin-section-season"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "season" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-bolt mr-1" />SEASON PASS
        </button>
        <button
          type="button"
          onClick={() => setSection("audit")}
          data-testid="admin-section-audit"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "audit" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-clipboard-list mr-1" />AUDIT
        </button>
        <button
          type="button"
          onClick={() => setSection("users")}
          data-testid="admin-section-users"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "users" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-user-shield mr-1" />USERS
        </button>
        <button
          type="button"
          onClick={() => setSection("payouts")}
          data-testid="admin-section-payouts"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "payouts" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-coins mr-1" />TIPS & PAYOUTS
        </button>
        <button
          type="button"
          onClick={() => setSection("onchain")}
          data-testid="admin-section-onchain"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "onchain" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-link mr-1" />ON-CHAIN
        </button>
        <button
          type="button"
          onClick={() => setSection("broadcast")}
          data-testid="admin-section-broadcast"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "broadcast" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-tower-broadcast mr-1" />BROADCAST
        </button>
        <button
          type="button"
          onClick={() => setSection("vouchers")}
          data-testid="admin-section-vouchers"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "vouchers" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-ticket mr-1" />VOUCHERS
        </button>
        <button
          type="button"
          onClick={() => setSection("analytics")}
          data-testid="admin-section-analytics"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${section === "analytics" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-chart-line mr-1" />ANALYTICS
        </button>
      </div>

      {section === "reports" ? (
        <ReportsSection
          data={data} tab={tab} setTab={setTab}
          decide={decide} restore={restore} decisionBusy={decisionBusy}
        />
      ) : section === "tickets" ? (
        <TicketsSection onToast={onToast} />
      ) : section === "leagues" ? (
        <LeaguesSection onToast={onToast} setAuthed={setAuthed} />
      ) : section === "disputes" ? (
        <DisputesSection onToast={onToast} setAuthed={setAuthed} />
      ) : section === "season" ? (
        <SeasonPassSection onToast={onToast} />
      ) : section === "audit" ? (
        <AuditSection onToast={onToast} />
      ) : section === "onchain" ? (
        <OnchainSection onToast={onToast} />
      ) : section === "payouts" ? (
        <PayoutsSection onToast={onToast} />
      ) : section === "users" ? (
        <UsersSection onToast={onToast} />
      ) : section === "broadcast" ? (
        <BroadcastSection onToast={onToast} />
      ) : section === "analytics" ? (
        <AnalyticsSection onToast={onToast} />
      ) : section === "vouchers" ? (
        <VouchersSection onToast={onToast} />
      ) : (
        <ReferralsSection onToast={onToast} setAuthed={setAuthed} />
      )}
    </main>
  );
}

export default AdminPanel;
